import { t } from 'i18next'
import moment from 'moment'
import { z } from 'zod'

import { TypesAllergy } from '@/modules/medicalRecord/domain/models/AllergyPatientMedicalRecord'
import { BloodType } from '@/modules/medicalRecord/domain/models/BloodType'
import { MedicationAdministration } from '@/modules/medicalRecord/domain/models/FullPrescriptionPatient'
import { Gender } from '@/modules/medicalRecord/domain/models/PatientProfileMedicalRecord'
import { FoodRestrictionType } from '@/modules/operational/modules/restriction/domain/models/FoodRestrictionType'
import { PatientSelectiveness } from '@/modules/operational/modules/restriction/domain/models/PatientSelectiveness'
import { RemedyType } from '@/modules/remedies/domain/models/RemedyTypeEnum'
import { GenericDocument } from '@/modules/shared/schemas/GenericDocument'
import { ResponsibleType } from '@/ui/modules/admin/views/Responsibles/responsibleType'

export const UpdateResponsibleMedicalRecordDTO = z.object({
  responsibleName: z.string(),
  responsibleDocument: GenericDocument,
  responsibleBirthday: z.string(),
  responsibleGender: Gender,
  responsibleType: ResponsibleType,
  patientBirthDay: z.string(),
  height: z.number(),
  weight: z.number(),
  bloodType: BloodType,
  patientGender: Gender,
  foods: z.object({
    patientSelective: PatientSelectiveness,
    foods: z.array(
      z.object({
        id: z.string().nullable(),
        food: z.string(),
        foodRestrictionType: FoodRestrictionType
      })
    )
  }),
  allergies: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string(),
      typeAllergy: TypesAllergy
    })
  ),
  comorbidities: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string()
    })
  ),
  medications: z.array(
    z.object({
      id: z.string().nullable(),
      medication: z.string(),
      activeIngredient: z.string(),
      typeMedicine: RemedyType,
      medicationAdministration: MedicationAdministration
    })
  )
})
export type UpdateResponsibleMedicalRecordDTO = z.infer<
  typeof UpdateResponsibleMedicalRecordDTO
>

export const UpdateResponsibleMedicalRecordForm = z.object({
  responsibleName: z
    .string()
    .min(1, {
      message: t('general.form.required')
    })
    .max(160, {
      message: t('general.form.maxValue', { value: 160 })
    }),
  responsibleDocument: GenericDocument,
  responsibleBirthday: z
    .string()
    .min(1, {
      message: t('general.form.required')
    })
    .refine(
      (v) => moment(v).isAfter(moment('1900-01-01'), 'day'),
      t('general.form.minDate', {
        date: moment('1900-01-01').format('L'),
        interpolation: { escapeValue: false }
      })
    )
    .refine(
      (v) =>
        moment(v).isSameOrBefore(
          moment()
            .subtract(18, 'year')
            .add(1, 'day')
            .format(moment.HTML5_FMT.DATE),
          'day'
        ),
      t('general.form.maxDate', {
        date: moment().subtract(18, 'year').add(1, 'day').format('L'),
        interpolation: { escapeValue: false }
      })
    ),
  responsibleGender: z.enum([...Gender.options], {
    message: t('general.form.required')
  }),
  responsibleType: z.enum([...ResponsibleType.options], {
    message: t('general.form.required')
  }),
  patientBirthDay: z
    .string()
    .min(1, {
      message: t('general.form.required')
    })
    .refine(
      (v) => moment(v).isAfter(moment('1900-01-01'), 'day'),
      t('general.form.minDate', {
        date: moment('1900-01-01').format('L'),
        interpolation: { escapeValue: false }
      })
    )
    .refine(
      (v) =>
        moment(v).isSameOrBefore(moment().format(moment.HTML5_FMT.DATE), 'day'),
      t('general.form.maxDate', {
        date: moment().format('L'),
        interpolation: { escapeValue: false }
      })
    ),
  height: z
    .number({
      message: t('general.form.required')
    })
    .min(1, {
      message: t('general.form.minValue', { value: 1 })
    })
    .max(240, {
      message: t('general.form.maxValue', { value: 240 })
    }),
  weight: z
    .number({
      message: t('general.form.required')
    })
    .min(1, {
      message: t('general.form.minValue', { value: 1 })
    })
    .max(500, {
      message: t('general.form.maxValue', { value: 500 })
    }),
  bloodType: z.enum([...BloodType.options], {
    message: t('general.form.required')
  }),
  patientGender: z.enum([...Gender.options], {
    message: t('general.form.required')
  }),
  allergies: z
    .array(
      z.object({
        id: z.string().nullable(),
        name: z
          .string()
          .min(1, {
            message: t('general.form.required')
          })
          .max(160, {
            message: t('general.form.maxValue', { value: 160 })
          }),
        typeAllergy: z.enum([...TypesAllergy.options], {
          message: t('general.form.required')
        })
      })
    )
    .superRefine((allergies, ctx) => {
      for (let i = 0; i < allergies.length; i++) {
        for (let j = i + 1; j < allergies.length; j++) {
          if (
            allergies[i].name.toLowerCase().trim() ===
            allergies[j].name.toLowerCase().trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'name'],
              message: t('general.form.duplicated')
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [j, 'name'],
              message: t('general.form.duplicated')
            })
          }
        }
      }
    }),
  medications: z
    .array(
      z.object({
        id: z.string().nullable(),
        medication: z
          .string()
          .min(1, {
            message: t('general.form.required')
          })
          .max(160, {
            message: t('general.form.maxValue', { value: 160 })
          }),
        activeIngredient: z
          .string()
          .min(1, {
            message: t('general.form.required')
          })
          .max(160, {
            message: t('general.form.maxValue', { value: 160 })
          }),
        typeMedicine: z.enum([...RemedyType.options], {
          message: t('general.form.required')
        }),
        medicationAdministration: z.enum(
          [...MedicationAdministration.options],
          {
            message: t('general.form.required')
          }
        )
      })
    )
    .superRefine((medications, ctx) => {
      for (let i = 0; i < medications.length; i++) {
        for (let j = i + 1; j < medications.length; j++) {
          if (
            medications[i].medication.toLowerCase().trim() ===
            medications[j].medication.toLowerCase().trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'medication'],
              message: t('general.form.duplicated')
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [j, 'medication'],
              message: t('general.form.duplicated')
            })
          }
        }
      }
    }),
  foods: z.object({
    patientSelective: z.enum([...PatientSelectiveness.options], {
      message: t('general.form.required')
    }),
    foods: z
      .array(
        z.object({
          id: z.string().nullable(),
          food: z
            .string()
            .min(1, {
              message: t('general.form.required')
            })
            .max(160, {
              message: t('general.form.maxValue', { value: 160 })
            }),
          foodRestrictionType: z.enum([...FoodRestrictionType.options], {
            message: t('general.form.required')
          })
        })
      )
      .superRefine((foods, ctx) => {
        for (let i = 0; i < foods.length; i++) {
          for (let j = i + 1; j < foods.length; j++) {
            if (
              foods[i].food.toLowerCase().trim() ===
              foods[j].food.toLowerCase().trim()
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [i, 'food'],
                message: t('general.form.duplicated')
              })
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [j, 'food'],
                message: t('general.form.duplicated')
              })
            }
          }
        }
      })
  }),
  comorbidities: z
    .array(
      z.object({
        id: z.string().nullable(),
        name: z
          .string()
          .min(1, {
            message: t('general.form.required')
          })
          .max(160, {
            message: t('general.form.maxValue', { value: 160 })
          })
      })
    )
    .superRefine((comorbidities, ctx) => {
      for (let i = 0; i < comorbidities.length; i++) {
        for (let j = i + 1; j < comorbidities.length; j++) {
          if (
            comorbidities[i].name.toLowerCase().trim() ===
            comorbidities[j].name.toLowerCase().trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'name'],
              message: t('general.form.duplicated')
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [j, 'name'],
              message: t('general.form.duplicated')
            })
          }
        }
      }
    })
})
export type UpdateResponsibleMedicalRecordForm = z.infer<
  typeof UpdateResponsibleMedicalRecordForm
>

export const ReviewResponsibleMedicalRecordForm = z.object({
  responsibleName: z.string(),
  responsibleDocument: GenericDocument,
  responsibleBirthday: z.string(),
  responsibleGender: Gender,
  responsibleType: ResponsibleType,
  patientBirthDay: z
    .string()
    .min(1, {
      message: t('general.form.required')
    })
    .optional()
    .refine((v) => v !== undefined, {
      message: t('general.form.required')
    }),
  height: z
    .number({
      message: t('general.form.required')
    })
    .positive({
      message: t('general.form.minValue', { value: 1 })
    })
    .optional()
    .refine((v) => v !== undefined, {
      message: t('general.form.required')
    }),
  weight: z
    .number({
      message: t('general.form.required')
    })
    .positive({
      message: t('general.form.minValue', { value: 1 })
    })
    .optional()
    .refine((v) => v !== undefined, {
      message: t('general.form.required')
    }),
  bloodType: z
    .enum([...BloodType.options], {
      message: t('general.form.required')
    })
    .optional()
    .refine((v) => v !== undefined, {
      message: t('general.form.required')
    }),
  patientGender: z
    .enum([...Gender.options], {
      message: t('general.form.required')
    })
    .optional()
    .refine((v) => v !== undefined, {
      message: t('general.form.required')
    }),
  allergies: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string().min(1, {
        message: t('general.form.required')
      }),
      typeAllergy: z.enum([...TypesAllergy.options], {
        message: t('general.form.required')
      })
    })
  ),
  medications: z.array(
    z.object({
      id: z.string().nullable(),
      medication: z.string().min(1, {
        message: t('general.form.required')
      }),
      activeIngredient: z.string().min(1, {
        message: t('general.form.required')
      }),
      typeMedicine: z.enum([...RemedyType.options], {
        message: t('general.form.required')
      }),
      medicationAdministration: z.enum([...MedicationAdministration.options], {
        message: t('general.form.required')
      })
    })
  ),
  foods: z.object({
    patientSelective: z
      .enum([...PatientSelectiveness.options], {
        message: t('general.form.required')
      })
      .optional()
      .refine((v) => v !== undefined, {
        message: t('general.form.required')
      }),
    foods: z.array(
      z.object({
        id: z.string().nullable(),
        food: z.string().min(1, {
          message: t('general.form.required')
        }),
        foodRestrictionType: z.enum([...FoodRestrictionType.options], {
          message: t('general.form.required')
        })
      })
    )
  }),
  comorbidities: z.array(
    z.object({
      id: z.string().nullable(),
      name: z.string().min(1, {
        message: t('general.form.required')
      })
    })
  )
})
export type ReviewResponsibleMedicalRecordForm = z.input<
  typeof ReviewResponsibleMedicalRecordForm
>
